import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ModeBadge } from "./ModeBadge";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Check, Clock, RefreshCw, Zap, TrendingUp, TrendingDown, ChevronDown, ChevronRight, X } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";
import { ExecutionJourney } from "./ExecutionJourney";
import { RejectionSummaryPanel } from "./RejectionSummaryPanel";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

const ScoreBadge = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const strong = value >= 80;
  const color = strong ? "var(--bull)" : "#FFB66E";
  return (
    <span
      className="inline-flex items-center gap-1 font-mono-data px-1.5 py-0.5 border"
      style={{ color, borderColor: color }}
    >
      {strong && <Zap size={11} />}
      {fmt(value, 0)}
    </span>
  );
};

const PnlPill = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const pos = value >= 0;
  return (
    <span className="font-mono-data" style={{ color: pos ? "var(--bull)" : "var(--bear)" }}>
      {pos ? "+" : ""}
      {fmt(value)}%
    </span>
  );
};

const OpenTradeRow = ({ t, i }) => {
  const [expanded, setExpanded] = useState(false);
  const hasJourney = Boolean(
    t.momentum_tier || t.buy_buffer_pct_actual || t.chase_status || t.live_buy_order_id
  );
  return (
    <React.Fragment>
      <tr className="border-b border-grid hover:bg-white/[0.02]" data-testid={`oi-autopilot-open-row-${i}`}>
        <td className="px-4 py-2">
          {hasJourney ? (
            <button
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? "Hide Execution Journey" : "Show Execution Journey"}
              data-testid={`oi-execution-journey-open-toggle-${i}`}
              className="text-muted-2 hover:text-primary"
              title="Toggle Execution Journey"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : null}
        </td>
        <td className="px-4 py-2 text-muted-2 font-mono-data">{t.slot}</td>
        <td className="px-4 py-2 font-semibold">{t.symbol}<ModeBadge t={t} /></td>
        <td className="px-4 py-2 text-right font-mono-data">{t.atm_strike} CE</td>
        <td className="px-4 py-2 text-right font-mono-data">₹{fmt(t.atm_ce_ltp)}</td>
        <td className="px-4 py-2 text-right font-mono-data">
          {t.current_ce_ltp ? `₹${fmt(t.current_ce_ltp)}` : <span className="text-muted-3">—</span>}
        </td>
        <td className="px-4 py-2 text-right"><PnlPill value={t.pnl_pct} /></td>
        <td className="px-4 py-2 text-right font-mono-data text-[10px]">
          <span style={{ color: "var(--bull)" }}>+{fmt(t.target_pct ?? 10, 1)}%</span>
          <span className="text-muted-3"> / </span>
          <span style={{ color: "var(--bear)" }}>{fmt(t.stop_loss_pct ?? -15, 1)}%</span>
        </td>
      </tr>
      {expanded && <ExecutionJourney trade={t} colSpan={8} />}
    </React.Fragment>
  );
};


const TradeRow = ({ t, i, groupTestid, onManualExit }) => {
  const [expanded, setExpanded] = useState(false);
  const live = t.current_ce_ltp || t.close_ce_ltp;
  const pnl = t.final_pnl_pct ?? t.pnl_pct;
  const isClosed = t.status === "CLOSED";
  const sold = t.live_trade && t.live_sell_order_id && !t.live_sell_error;
  let statusLabel;
  if (!isClosed) statusLabel = "OPEN";
  else if (t.close_reason === "PROFIT_BOOKED") statusLabel = "BOOKED";
  else if (t.close_reason === "STOP_LOSS") statusLabel = "STOP LOSS";
  else if (t.close_reason === "MULTI_TF_REVERSAL") statusLabel = "MTF REVERSAL";
  else if (t.close_reason === "MANUAL_EXIT") statusLabel = sold ? "SOLD" : "MANUAL EXIT";
  else if (t.close_reason === "EOD") statusLabel = "EOD CLOSE";
  else if (t.close_reason === "BUY_CHASE_TIMEOUT") statusLabel = "BUY TIMEOUT";
  else statusLabel = t.close_reason || "CLOSED";
  const hasJourney = Boolean(
    t.momentum_tier || t.buy_buffer_pct_actual || t.chase_status || t.live_buy_order_id
  );
  return (
    <React.Fragment>
      <tr data-testid={`${groupTestid}-row-${i}`} className="border-b border-grid hover:bg-white/[0.02]">
        <td className="px-4 py-2">
          {hasJourney ? (
            <button
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? "Hide Execution Journey" : "Show Execution Journey"}
              data-testid={`oi-execution-journey-toggle-${groupTestid}-${i}`}
              className="text-muted-2 hover:text-primary"
              title="Toggle Execution Journey"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : null}
        </td>
        <td className="px-4 py-2 text-muted-2 font-mono-data">{fmtISTDateTime(t.entered_at)}</td>
        <td className="px-4 py-2 font-semibold">
          {t.symbol}
          <ModeBadge t={t} />
        </td>
        <td className="px-4 py-2 text-right font-mono-data">{t.atm_strike} CE</td>
        <td className="px-4 py-2 text-right font-mono-data">₹{fmt(t.atm_ce_ltp)}</td>
        <td className="px-4 py-2 text-right font-mono-data">{live ? `₹${fmt(live)}` : "—"}</td>
        <td className="px-4 py-2 text-right"><PnlPill value={pnl} /></td>
        <td className="px-4 py-2 text-right">
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border"
            style={{
              color: !isClosed
                ? "var(--bull)"
                : statusLabel === "BOOKED" || statusLabel === "SOLD"
                ? "var(--bull)"
                : statusLabel === "STOP_LOSS"
                ? "var(--bear)"
                : "var(--text-muted)",
              borderColor: !isClosed ? "var(--bull)" : "var(--text-muted)",
            }}
            title={t.sell_skipped_reason ? `Sell skipped — ${t.sell_skipped_reason}` : (t.close_reason || "")}
          >
            {!isClosed && <Clock size={11} />}
            {statusLabel}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          {!isClosed && t.id ? (
            <button
              data-testid={`oi-manual-exit-${groupTestid}-${i}`}
              onClick={() => onManualExit && onManualExit(t.id, t.symbol)}
              className="text-[10px] uppercase tracking-wider font-mono-data px-2.5 py-1 border hover:bg-white/5"
              style={{ color: "var(--text-primary)", borderColor: "var(--border-color)" }}
              title={t.live_trade ? "Manual Exit — validates position then places SELL on Upstox" : "Manual Exit — paper close at current LTP"}
            >
              Exit
            </button>
          ) : (
            <span className="text-muted-3 text-[10px]">—</span>
          )}
        </td>
      </tr>
      {expanded && <ExecutionJourney trade={t} colSpan={9} />}
    </React.Fragment>
  );
};

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    // If the ISO string has no timezone marker, assume UTC (Phase 24 — Mongo
    // strips tzinfo on read; backend now appends 'Z' but stay defensive).
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(s));
  } catch {
    return "—";
  }
};

// Phase 26: full date+time IST for Paper Trade History (e.g. "06 Jun 14:32")
const fmtISTDateTime = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(new Date(s)).replace(",", "");
  } catch {
    return "—";
  }
};

// 2026-08 — diagnostic panel so a low/zero-pick session can be reviewed
// instead of guessed at. `funnel` shows how many symbols survived each
// stage of the pipeline; `nearMiss` shows the actual score breakdown for
// the ~12 strongest OI-signal candidates each scan, whether or not they
// became a qualifying pick. Collapsed by default — this is for occasional
// review, not everyday viewing.
const OIFunnelDiagnostics = ({ funnel, nearMiss, slot }) => {
  if (!funnel && (!nearMiss || nearMiss.length === 0)) return null;
  const stages = [
    { k: "raw_feed", label: "F&O symbols with OI data" },
    { k: "oi_positive", label: "OI rising (long-buildup direction)" },
    { k: "price_enriched", label: "Price up 0–8% today" },
    { k: "refine_pool", label: "Top-12 by OI signal" },
    { k: "vwap_unavailable", label: "Dropped — VWAP data unavailable", negative: true },
    { k: "vwap_hard_dropped", label: "Dropped — below VWAP or too extended", negative: true },
    { k: "passed_quality_gate", label: "Passed score/RVOL/Nifty gate" },
    { k: "weak_pool", label: "Routed to weak pool" },
    { k: "final_returned", label: "Returned as suggestions" },
  ];
  return (
    <details className="border border-grid p-4 space-y-3" data-testid="oi-funnel-diagnostics">
      <summary className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data cursor-pointer">
        Funnel diagnostics · Last slot: {slot || "—"}
      </summary>
      {funnel && (
        <div className="flex flex-wrap gap-2 pt-2">
          {stages.filter((st) => funnel[st.k] != null).map((st) => (
            <span key={st.k}
              className="inline-flex items-center gap-1.5 px-2 py-1 border border-grid text-[10px] font-mono-data"
              title={st.label}>
              <span style={{ color: st.negative ? "var(--bear)" : "var(--bull)" }}>{funnel[st.k]}</span>
              <span className="text-muted-2">{st.label}</span>
            </span>
          ))}
        </div>
      )}
      {nearMiss && nearMiss.length > 0 && (
        <div className="overflow-x-auto pt-1">
          <table className="w-full text-[11px] font-mono-data">
            <thead>
              <tr className="border-b border-grid text-muted-3 uppercase tracking-wider text-[9px]">
                <th className="text-left px-2 py-1.5">Symbol</th>
                <th className="text-right px-2 py-1.5">OI Signal %</th>
                <th className="text-right px-2 py-1.5">Price %</th>
                <th className="text-left px-2 py-1.5">Quality</th>
                <th className="text-right px-2 py-1.5">Raw Score</th>
                <th className="text-right px-2 py-1.5">Adj. Score</th>
                <th className="text-left px-2 py-1.5">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {nearMiss.map((n, i) => (
                <tr key={`${n.symbol}-${i}`} className="border-b border-grid/40">
                  <td className="px-2 py-1.5 text-muted-1">{n.symbol}</td>
                  <td className="px-2 py-1.5 text-right text-muted-2">{fmt(n.oi_signal_pct, 2)}</td>
                  <td className="px-2 py-1.5 text-right text-muted-2">{fmt(n.price_change_pct, 2)}</td>
                  <td className="px-2 py-1.5 text-muted-2">{n.entry_quality || "—"}</td>
                  <td className="px-2 py-1.5 text-right text-muted-2">{fmt(n.raw_score, 2)}</td>
                  <td className="px-2 py-1.5 text-right"
                      style={{ color: n.passes_own_gate ? "var(--bull)" : "var(--bear)" }}>
                    {fmt(n.adjusted_score, 2)}
                  </td>
                  <td className="px-2 py-1.5 text-muted-3">
                    {n.dropped_reason || (n.weak_signal_reasons || []).join("; ") || (n.passes_own_gate ? "passed" : "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
};

export const OIAutopilot = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [trades, setTrades] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [collapsed, toggle] = useCollapsed("oi-autopilot", false);
  // Phase 49b — Per-table Show/Hide toggles
  const [openCollapsed, toggleOpen] = useCollapsed("oi-autopilot.open", false);
  const [picksCollapsed, togglePicks] = useCollapsed("oi-autopilot.picks", false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, tradesRes] = await Promise.all([
        axios.get(`${API}/oi-autopilot`),
        axios.get(`${API}/oi-autopilot/paper-trades`),
      ]);
      setData(todayRes.data);
      setTrades(tradesRes.data);
    } catch (e) {
      toast.error("Failed to load OI autopilot");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const onScan = useCallback(async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/oi-autopilot/scan`);
      const entered = (r?.data?.run?.entered || []).length;
      const qual = r?.data?.run?.qualifying_count ?? 0;
      toast.success(`OI scan: ${qual} qualifying, ${entered} auto-entered`, { duration: 3000 });
      await load();
    } catch (e) {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  }, [load]);

  // Phase 26: Section 6 live-trading kill-switch (independent of Section 5)
  const [liveBusy, setLiveBusy] = useState(false);
  const liveEnabled = Boolean(data?.live_trading_enabled);
  const onToggleLive = useCallback(async () => {
    const next = !liveEnabled;
    const verb = next ? "ENABLE" : "DISABLE";
    if (!window.confirm(
      `${verb} LIVE trading for Section 6 (OI Autopilot)?\n\n` +
      (next
        ? "From now on, auto-picks will place REAL Upstox BUY orders (Delivery / NRML, 1 lot ATM Call)."
        : "New auto-entries will be paper-only.\nExisting OPEN live positions will still be SOLD at TP / SL / EOD.")
    )) return;
    setLiveBusy(true);
    try {
      await axios.post(`${API}/oi-autopilot/live-status`, { enabled: next });
      toast.success(next ? "OI live trading ENABLED" : "OI live trading DISABLED");
      await load();
    } catch {
      toast.error("Failed to flip OI live-trading flag");
    } finally {
      setLiveBusy(false);
    }
  }, [liveEnabled, load]);

  // Phase 26: per-trade Manual Exit (mirrors Section 5)
  const onManualExit = useCallback(async (tradeId, symbol) => {
    if (!window.confirm(`Manually exit ${symbol}?\n\nThis will close the trade at the latest LTP. Live trades will trigger a SELL on Upstox (only if a position still exists).`)) return;
    try {
      const r = await axios.post(`${API}/oi-autopilot/manual-exit`, { trade_id: tradeId });
      const trade = r?.data?.trade;
      const pnl = trade?.final_pnl_pct;
      const skipped = trade?.sell_skipped_reason === "no_position";
      toast.success(
        `${symbol}: Manual exit${pnl != null ? ` · P&L ${pnl >= 0 ? "+" : ""}${pnl}%` : ""}` +
        (skipped ? " · sell skipped (no Upstox position)" : "")
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Manual exit failed";
      toast.error(`${symbol}: ${detail}`);
    }
  }, [load]);

  // Phase 24: Dedupe qualifying picks by symbol (keep the latest occurrence per
  // stock so we never repeat the same name), then sort by Multi-TF desc, then
  // Score desc, and surface only the top 2 stocks of the day.
  const rows = useMemo(() => {
    if (!data?.runs) return [];
    // Runs are returned newest-first by ran_at_utc; iterate so the first time
    // we see a symbol is its latest signal.
    const latestBySym = new Map();
    for (const run of data.runs) {
      for (const q of run.qualifying || []) {
        const sym = q.symbol;
        if (!sym || latestBySym.has(sym)) continue;
        const auto = (run.entered || []).find((e) => e.symbol === sym);
        latestBySym.set(sym, {
          ...q,
          slot: run.slot,
          ran_at_ist: run.ran_at_ist,
          ran_at_utc: run.ran_at_utc,
          auto_entered: Boolean(auto),
        });
      }
    }
    const deduped = Array.from(latestBySym.values());
    deduped.sort((a, b) => {
      const mtfA = a?.multi_tf?.bullish_count ?? 0;
      const mtfB = b?.multi_tf?.bullish_count ?? 0;
      if (mtfB !== mtfA) return mtfB - mtfA;
      return (b?.score ?? 0) - (a?.score ?? 0);
    });
    return deduped.slice(0, 2);
  }, [data]);

  const openTrades = data?.open_trades || [];
  const cap = data?.max_auto_picks_per_day ?? 2;
  const usedToday = data?.auto_today_count ?? 0;

  return (
    <section className="space-y-4" data-testid="oi-autopilot-section">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            06 / Auto-Pilot · OI Long-Buildup
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            OI Long-Buildup · 5-Min Auto-Picks
          </h2>
          <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="oi-autopilot-description">
              <strong style={{ color: liveEnabled ? "var(--bull)" : "var(--text-primary)" }}>
                {liveEnabled ? "LIVE trading" : "Autopilot (Paper)"}
              </strong>{" "}· Every
              5 min from {data?.scan_window_ist || "09:30-14:30"} IST we re-run the
              Upstox OI long-buildup feed and auto-enter the top stocks that pass{" "}
              <strong>all 4 admin-configurable filters</strong>: score ≥ {data?.min_score ?? 5},
              multi-TF ≥ {data?.min_bullish_tf ?? 2} of 5m/1h/1d bullish,
              VWAP-distance &lt; {data?.max_vwap_dist_pct ?? 5}%, and sector trend not bearish.
              Thresholds editable live from <strong>Admin → Configuration → Section 6</strong>{" "}
              without redeploy. Up to{" "}
              <strong>{cap} trades/day</strong> (parallel allowed).{" "}
              <strong style={{ color: "var(--bear)" }}>No new entries after 14:00 IST.</strong>{" "}
              <strong>Dynamic target +8% to +14%</strong> & SL{" "}
              <strong style={{ color: "var(--bear)" }}>-10% to -20%</strong> per stock
              (sector + market + ATR + multi-TF). Auto-closes at 15:15 IST EOD.
              <strong> Option chase guard</strong>: skips ATM Call strikes already up ≥50% from today's open.{" "}
              A pick can pass all 4 filters above and still not enter — several more real-time checks run
              just before entry (correction mode, contract quality, bid-ask spread, option-chase guard,
              price-move gate, risk:reward floor, daily cap). Hover a <strong>Skipped</strong> status below to see which one applied.
            </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!collapsed && (
            <button
              data-testid="oi-autopilot-live-toggle"
              onClick={onToggleLive}
              disabled={liveBusy}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono-data px-3 py-1.5 border disabled:opacity-50"
              style={{
                color: liveEnabled ? "var(--bull)" : "var(--text-muted)",
                borderColor: liveEnabled ? "var(--bull)" : "var(--border-color)",
              }}
              title={liveEnabled ? "Section 6 LIVE — real Upstox BUYs. Click to switch to PAPER." : "Section 6 PAPER — click to enable real Upstox BUYs."}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: liveEnabled ? "var(--bull)" : "var(--text-muted)" }}
              />
              Live · {liveEnabled ? "ON" : "OFF"}
            </button>
          )}
          {data?.next_scan_at && !collapsed && (
            <span className="text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border border-grid text-muted-2">
              Next · {data.next_scan_at}
            </span>
          )}
          {!collapsed && (
            <button
              data-testid="oi-autopilot-scan-btn"
              onClick={onScan}
              disabled={scanning}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono-data px-3 py-1.5 border border-grid hover:bg-white/5 disabled:opacity-50"
            >
              <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
              {scanning ? "Scanning…" : "Run Scan Now"}
            </button>
          )}
          <CollapseToggle collapsed={collapsed} onToggle={toggle} testId="oi-autopilot-collapse" />
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Status strip */}
          <div className="border-grid surface px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] uppercase tracking-wider font-mono-data">
            <span className="text-muted-3">Window IST · {data?.scan_window_ist || "09:30-14:30"} · every {data?.scan_interval_min ?? 5} min</span>
            <span className="ml-auto text-muted-3">
              Open · {openTrades.length} · Auto today · {usedToday}/{cap} ·{" "}
              <span style={{ color: "var(--bull)" }}>TP +8 to +14%</span> ·{" "}
              <span style={{ color: "var(--bear)" }}>SL -10 to -20%</span> · EOD {data?.eod_close_at || "15:15 IST"}
            </span>
          </div>

          {/* Open trades */}
          {openTrades.length > 0 && (
            <div className="border-grid surface overflow-x-auto">
              <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-wider font-mono-data text-muted-3 flex items-center justify-between gap-2">
                <span>Open trades · live P&L</span>
                <CollapseToggle
                  collapsed={openCollapsed}
                  onToggle={toggleOpen}
                  testId="oi-autopilot-open-toggle"
                />
              </div>
              {!openCollapsed && (
              <table className="w-full text-xs" data-testid="oi-autopilot-open-table">
                <thead>
                  <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                    <th className="text-left px-4 py-2 w-8"></th>
                    <th className="text-left px-4 py-2">Slot</th>
                    <th className="text-left px-4 py-2">Symbol</th>
                    <th className="text-right px-4 py-2">Strike</th>
                    <th className="text-right px-4 py-2">Entry LTP</th>
                    <th className="text-right px-4 py-2">Current LTP</th>
                    <th className="text-right px-4 py-2">P&L</th>
                    <th className="text-right px-4 py-2">TP / SL</th>
                  </tr>
                </thead>
                <tbody>
                  {openTrades.map((t, i) => (
                    <OpenTradeRow key={i} t={t} i={i} />
                  ))}
                </tbody>
              </table>
              )}
            </div>
          )}

          {/* Qualifying picks per run */}
          {loading && !data ? (
            <div className="border-grid surface p-4 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <>
              <div className="border-grid surface p-6 text-center">
                <Activity size={20} className="mx-auto mb-2 text-muted-3" />
                <p className="text-sm text-muted-2">
                  No qualifying picks yet. Scans run every 5 min between 09:30 and 14:30 IST
                  on weekdays — or hit "Run Scan Now" to fire one manually.
                </p>
              </div>
              <RejectionSummaryPanel
                testid="oi-autopilot-rejection-summary"
                title="Why no OI picks?"
                slot={data?.runs?.[0]?.slot}
                universeSize={data?.runs?.[0]?.universe_size ?? data?.runs?.[0]?.scanned}
                rejectionSummary={data?.runs?.[0]?.rejection_summary || {}}
              />
            </>
          ) : (
            <div className="border-grid surface overflow-x-auto">
              <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-wider font-mono-data text-muted-3 flex items-center justify-between gap-2">
                <span>Today&apos;s Qualifying Picks · {rows.length}</span>
                <CollapseToggle
                  collapsed={picksCollapsed}
                  onToggle={togglePicks}
                  testId="oi-autopilot-picks-toggle"
                />
              </div>
              {!picksCollapsed && (
              <table className="w-full text-xs" data-testid="oi-autopilot-table">
                <thead>
                  <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                    <th className="text-left px-4 py-2 w-12">Sr.</th>
                    <th className="text-left px-4 py-2">Time IST</th>
                    <th className="text-left px-4 py-2">Symbol</th>
                    <th className="text-right px-4 py-2">Price</th>
                    <th className="text-right px-4 py-2">% Chg</th>
                    <th className="text-right px-4 py-2">Score</th>
                    <th className="text-right px-4 py-2">VWAP Δ</th>
                    <th className="text-left px-4 py-2">Multi-TF</th>
                    <th className="text-left px-4 py-2">Sector</th>
                    <th className="text-right px-4 py-2">Strike</th>
                    <th className="text-right px-4 py-2">CE LTP</th>
                    <th className="text-right px-4 py-2" title="Dynamic profit target">TP %</th>
                    <th className="text-right px-4 py-2" title="Dynamic stop-loss">SL %</th>
                    <th className="text-right px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const sectorTrend = r?.sector?.trend?.trend;
                    const sectorBearish = sectorTrend === "BEARISH";
                    const sectorOk = !sectorBearish;
                    const mtfCount = r?.multi_tf?.bullish_count;
                    const minMtf = data?.min_bullish_tf ?? 2;
                    const atmLtp = r?.option?.atm?.ltp;
                    return (
                      <tr key={`${r.slot}-${r.symbol}-${i}`} className="border-b border-grid hover:bg-white/[0.02]" data-testid={`oi-autopilot-row-${i}`}>
                        <td className="px-4 py-2 text-muted-3 font-mono-data">{i + 1}</td>
                        <td className="px-4 py-2 text-muted-2 font-mono-data">{fmtIST(r.ran_at_utc)}</td>
                        <td className="px-4 py-2 font-semibold">{r.symbol}</td>
                        <td className="px-4 py-2 text-right font-mono-data">₹{fmt(r.last_price)}</td>
                        <td className="px-4 py-2 text-right font-mono-data" style={{ color: (r.change_pct || 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                          {r.change_pct >= 0 ? "+" : ""}{fmt(r.change_pct)}%
                        </td>
                        <td className="px-4 py-2 text-right"><ScoreBadge value={r.score} /></td>
                        <td className="px-4 py-2 text-right font-mono-data">{fmt(r.vwap_distance_pct)}%</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1 font-mono-data text-[10px]"
                            style={{ color: (mtfCount ?? 0) >= minMtf ? "var(--bull)" : "var(--text-muted)" }}>
                            <Check size={11} /> {mtfCount}/3
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1 font-mono-data text-[10px]"
                            style={{ color: sectorOk ? "var(--bull)" : "var(--text-muted)" }}>
                            {sectorOk ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                            {r?.sector?.name || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data">{r?.option?.atm_strike ? `${r.option.atm_strike} CE` : "—"}</td>
                        <td className="px-4 py-2 text-right font-mono-data">{atmLtp ? `₹${fmt(atmLtp)}` : "—"}</td>
                        <td className="px-4 py-2 text-right font-mono-data"
                            title={r.levels_breakdown ? `Sector ${sectorTrend || '—'} · Market ${r.market_sentiment || '—'} · ATR ${r.atr_pct ?? '—'}% · MTF ${mtfCount}/3` : undefined}
                            style={{ color: "var(--bull)" }}>
                          {r.target_pct ? `+${fmt(r.target_pct, 1)}%` : "—"}
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data" style={{ color: "var(--bear)" }}>
                          {r.stop_loss_pct ? `${fmt(r.stop_loss_pct, 1)}%` : "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {r.auto_entered ? (
                            <span
                              data-testid={`oi-autopilot-auto-${i}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border"
                              style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                            >
                              <Check size={11} /> Auto-Paper
                            </span>
                          ) : r.skip_reason ? (
                            // 2026-08 — a pick can pass all 4 filters shown above and
                            // still be skipped by a later gate (correction mode,
                            // option-chase guard, spread, R:R floor, daily cap, etc.)
                            // that the description text doesn't mention per-row.
                            // Surface the actual reason instead of a blank "—".
                            <span
                              data-testid={`oi-autopilot-skip-${i}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border cursor-help"
                              style={{ color: "var(--text-muted)", borderColor: "var(--border-color)" }}
                              title={r.skip_reason}
                            >
                              <X size={11} /> Skipped
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider font-mono-data text-muted-3">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
              <div className="px-4 py-2 border-t border-grid text-[10px] font-mono-data text-muted-3">
                {rows.length} qualifying picks · newest first
              </div>
            </div>
          )}

          <OIFunnelDiagnostics
            funnel={data?.runs?.[0]?.funnel}
            nearMiss={data?.runs?.[0]?.near_miss}
            slot={data?.runs?.[0]?.slot}
          />

          {/* Trade History (today + earlier) */}
          <OIPaperHistory trades={trades} onManualExit={onManualExit} />
        </>
      )}
    </section>
  );
};

const OIPaperHistory = ({ trades, onManualExit }) => {
  const today = trades?.today || [];
  const earlier = trades?.earlier || [];
  const total = today.length + earlier.length;
  const [open, setOpen] = useState(false);

  const groups = [
    { label: "Today", items: today, testid: "oi-history-today" },
    { label: "Earlier", items: earlier, testid: "oi-history-earlier" },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="border-grid surface" data-testid="oi-autopilot-history">
      <button
        data-testid="oi-history-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/[0.02]"
      >
        <span>Paper Trade History · last 2 days · {total} trade(s)</span>
        <span style={{ color: "var(--text-muted)" }}>{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div>
          {groups.length === 0 ? (
            <div className="px-4 py-4 text-xs text-muted-3">No trades yet.</div>
          ) : (
            groups.map((g) => (
              <div key={g.label}>
                <div className="px-4 py-1.5 border-y border-grid text-[10px] uppercase tracking-wider font-mono-data text-muted-3">
                  {g.label} ({g.items.length})
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                      <th className="text-left px-4 py-2 w-8"></th>
                      <th className="text-left px-4 py-2">Entry IST</th>
                      <th className="text-left px-4 py-2">Symbol</th>
                      <th className="text-right px-4 py-2">Strike</th>
                      <th className="text-right px-4 py-2">Entry LTP</th>
                      <th className="text-right px-4 py-2">Close/Curr LTP</th>
                      <th className="text-right px-4 py-2">P&amp;L</th>
                      <th className="text-right px-4 py-2">Status</th>
                      <th className="text-right px-4 py-2 w-24">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((t, i) => (
                      <TradeRow
                        key={i}
                        t={t}
                        i={i}
                        groupTestid={g.testid}
                        onManualExit={onManualExit}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};